/**
 * Slot Changeable Fields Rules
 *
 * Applies slot availability to changeable fields.
 * This is the final layer of rules applied after all other business rules.
 *
 * Logic:
 * - If a field requires manual confirmation AND has an available slot,
 *   upgrade it to auto-confirmable (no manual approval needed)
 * - If no slot is available, keep the original state
 */

import {useMemo} from 'react'

import {FieldNames, findFieldName} from '../../../../fields'
import {ChangeableFields} from '../../changeableFields/types'
import {SLOT_MANAGEMENT_DEPENDENT_FIELDS} from '../../../../slotManagement/consts'


export interface UseSlotChangeableFieldsRulesOptions {
  isSlotManagementEnabled: boolean
  /** Whether the selected slot is available */
  isSlotAvailable: boolean
}

/**
 * Hook that applies slot availability rules to changeable fields.
 *
 * This is the final layer of rules in the changeable fields system.
 * It should be called after all other business rules have been applied.
 *
 * @param changeableFields - Fields after all other business rules
 * @param options - Slot availability options
 * @returns Adjusted changeable fields with slot rules applied
 */
export const useSlotChangeableFieldsRules = (
  changeableFields: ChangeableFields,
  {isSlotManagementEnabled, isSlotAvailable}: UseSlotChangeableFieldsRulesOptions
): ChangeableFields => {
  return useMemo(() => {
    if (!isSlotManagementEnabled || !isSlotAvailable) {
      return changeableFields
    }

    return applySlotAvailabilityToFields(changeableFields)
  }, [changeableFields, isSlotAvailable])
}

/**
 * Checks if a field name is affected by slot availability.
 */
const isSlotAffectedField = (fieldName: FieldNames): boolean => {
  return !!findFieldName(SLOT_MANAGEMENT_DEPENDENT_FIELDS, fieldName);
}

/**
 * Applies slot availability to changeable fields.
 */
const applySlotAvailabilityToFields = (changeableFields: ChangeableFields): ChangeableFields =>
  changeableFields.map((field) => {
    if (!isSlotAffectedField(field.fieldName)) {
      return field
    }

    if (field.state !== 'requireDispatchConfirmation') {
      return field
    }

    return {
      ...field,
      state: 'autoConfirmable' as const
    }
  })
